import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  DISTRICT_MODES,
  getModeLabel,
  getModeTemplate,
  type DistrictMode,
  type FieldDef,
  type ModeProfile,
  type ModeProfileRecommendedMasters,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { useDistrict, useDistricts } from '../districts-admin';
import { ModeProfileApplyModal } from './ModeProfileApplyModal';
import { archiveModeProfile, upsertModeProfile, useModeProfiles } from './useModeProfiles';
import {
  useAiAnalysisSettings,
  useAiCategoryMasters,
  useAiDialogueSettings,
  useAiOutputFormats,
  useAnalysisDisplayConfigs,
  useScoreDimensionMasters,
} from './useMasters';
import { effectiveModelName } from './aiM1Subforms';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
// 使用状況の pill バッジ (M3 / M5 マスタ一覧と共用)。
import { UsageBadges, type UsageBadgeItem } from './components/UsageBadges';
// 別名で保存 (複製) / ドキュメント ID の検証 / 使用状況の集計は M1・M3・M5 と同じ純関数を再利用する。
import {
  DUPLICATE_NAME_SUFFIX,
  deepCloneDraft,
  groupModeProfileUsage,
  uniqueSlug,
  validateDocumentId,
  type ModeProfileUsage,
} from './aiAnalysisEditorHelpers';

/**
 * Sprint BG-DataSettings 30 + B2 (issue #32): ModeProfile (modeTemplates の動的化版) 管理画面。
 *
 * 旧 v1: 4 mode の初期 userFields/postFields は `modeTemplates.ts` static、code 変更必須。
 * 新方式: Firestore `modeProfiles/{id}` を CRUD、新地区作成時に snapshot コピー。
 *
 * B2 拡張:
 *   - mode は組込 4 種に限らず custom key (例 fukushi) を自由入力可 (datalist で組込を補完)。
 *   - custom モードの利用者表示名は modeLabel に持つ (getModeLabel の customLabel)。
 *   - 推奨マスタ ID は各マスタ一覧からの dropdown 選択 (旧: id 手入力)。
 *   - userFields / postFields は「現行テンプレ取込」に加え「既存地区から取込」可能。
 *   - userProfileOptions フォームは撤去 (userFields の relationship/attribute options で網羅、
 *     実消費ゼロの冗長フィールドだったため。docs/15 §M10 参照)。
 */

interface FormDraft {
  id: string;
  mode: DistrictMode;
  modeLabel: string;
  name: string;
  description: string;
  profileVersion: string;
  userFieldsJson: string;
  postFieldsJson: string;
  recommendedAnalysisSettingsId: string;
  recommendedDialogueSettingsId: string;
  recommendedDisplayConfigId: string;
  recommendedOutputFormatId: string;
  recommendedCategoryMasterIds: string;
  recommendedScoreDimensionId: string;
}

const EMPTY_DRAFT: FormDraft = {
  id: '',
  mode: 'ecw',
  modeLabel: '',
  name: '',
  description: '',
  profileVersion: '',
  userFieldsJson: '[]',
  postFieldsJson: '[]',
  recommendedAnalysisSettingsId: '',
  recommendedDialogueSettingsId: '',
  recommendedDisplayConfigId: '',
  recommendedOutputFormatId: '',
  recommendedCategoryMasterIds: '',
  recommendedScoreDimensionId: '',
};

function draftFromProfile(p: ModeProfile): FormDraft {
  return {
    id: p.id,
    mode: p.mode,
    modeLabel: p.modeLabel ?? '',
    name: p.name,
    description: p.description ?? '',
    profileVersion: p.profileVersion,
    userFieldsJson: JSON.stringify(p.userFields, null, 2),
    postFieldsJson: JSON.stringify(p.postFields, null, 2),
    recommendedAnalysisSettingsId: p.recommendedMasters?.analysisSettingsId ?? '',
    recommendedDialogueSettingsId: p.recommendedMasters?.dialogueSettingsId ?? '',
    recommendedDisplayConfigId: p.recommendedMasters?.displayConfigId ?? '',
    recommendedOutputFormatId: p.recommendedMasters?.outputFormatId ?? '',
    recommendedCategoryMasterIds: (p.recommendedMasters?.categoryMasterIds ?? []).join(', '),
    recommendedScoreDimensionId: p.recommendedMasters?.scoreDimensionId ?? '',
  };
}

function buildRecommendedMasters(draft: FormDraft): ModeProfileRecommendedMasters | undefined {
  const out: ModeProfileRecommendedMasters = {};
  const a = draft.recommendedAnalysisSettingsId.trim();
  if (a) out.analysisSettingsId = a;
  const dial = draft.recommendedDialogueSettingsId.trim();
  if (dial) out.dialogueSettingsId = dial;
  const dc = draft.recommendedDisplayConfigId.trim();
  if (dc) out.displayConfigId = dc;
  const of = draft.recommendedOutputFormatId.trim();
  if (of) out.outputFormatId = of;
  const ids = draft.recommendedCategoryMasterIds
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (ids.length > 0) out.categoryMasterIds = ids;
  const sd = draft.recommendedScoreDimensionId.trim();
  if (sd) out.scoreDimensionId = sd;
  return Object.keys(out).length > 0 ? out : undefined;
}

const TEXTAREA_CLASS =
  'w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y';
const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400';

/** 一覧 item を「選択中 mode に一致するものを上位」に並べる (custom モードは全件下位)。 */
function sortByModeMatch<T extends { displayName: string; mode: string }>(
  items: readonly T[],
  mode: string,
): T[] {
  return [...items].sort((a, b) => {
    const am = a.mode === mode ? 0 : 1;
    const bm = b.mode === mode ? 0 : 1;
    if (am !== bm) return am - bm;
    return a.displayName.localeCompare(b.displayName);
  });
}

/**
 * 推奨マスタ dropdown の option 元データ。
 * `generationConfig` は M1 (AI 解析設定) のみが持つ任意項目で、`showModel` 指定時だけ
 * 「実際に使われるモデル名」の表示に使う (M2〜M6 の表示は従来どおり)。
 */
type MasterOption = {
  id: string;
  displayName: string;
  mode: string;
  generationConfig?: { model?: string };
};

/** 推奨マスタ単一選択 dropdown (mode 一致を上位、現在値がアーカイブ済でも保持)。 */
function MasterSelect(props: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  items: readonly MasterOption[];
  mode: string;
  /** true のとき「実際に使われるモデル名」を option に併記する (M1 専用)。 */
  showModel?: boolean;
}) {
  const sorted = sortByModeMatch(props.items, props.mode);
  const modelNote = (it: MasterOption) => {
    if (!props.showModel) return '';
    const { model, isDefault } = effectiveModelName(it.generationConfig);
    return ` モデル=${model}${isDefault ? ' (既定)' : ''}`;
  };
  const valueInList = !props.value || sorted.some((it) => it.id === props.value);
  return (
    // min-w-0: 選択肢 1 行が「表示名 (id)［モード］モデル=…」と長いため、これが無いと
    // grid/flex の最小幅が選択肢の長さで決まり、フォーム全体が画面幅を超える。
    <label className="grid grid-cols-1 gap-1 min-w-0">
      <span className="text-xs text-slate-500">{props.label}</span>
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className={`${INPUT_CLASS} min-w-0`}
      >
        <option value="">(未選択)</option>
        {sorted.map((it) => (
          <option key={it.id} value={it.id}>
            {it.displayName} ({it.id}){it.mode !== props.mode ? `［${getModeLabel(it.mode)}］` : ''}
            {modelNote(it)}
          </option>
        ))}
        {!valueInList && (
          <option value={props.value}>{props.value} (一覧外 / アーカイブ済の可能性)</option>
        )}
      </select>
      {props.hint && <span className="text-[11px] text-slate-400">{props.hint}</span>}
    </label>
  );
}

/** 推奨カテゴリマスタ (M2) の複数選択。comma 区切り文字列で保持。 */
function MasterMultiSelect(props: {
  label: string;
  selectedCsv: string;
  onChange: (csv: string) => void;
  items: readonly MasterOption[];
  mode: string;
}) {
  const selected = props.selectedCsv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const selSet = new Set(selected);
  const sorted = sortByModeMatch(props.items, props.mode);
  function toggle(id: string, checked: boolean) {
    // 選択順を維持: 既存から除去し、ON なら末尾に追加。
    const next = selected.filter((x) => x !== id);
    if (checked) next.push(id);
    props.onChange(next.join(', '));
  }
  return (
    <div className="grid grid-cols-1 gap-1 min-w-0">
      <span className="text-xs text-slate-500">{props.label}</span>
      {sorted.length === 0 ? (
        <p className="text-[11px] text-slate-400">選択できるカテゴリマスタがありません。</p>
      ) : (
        <div className="grid grid-cols-1 gap-1 min-w-0 max-h-40 overflow-auto border border-slate-200 rounded p-2">
          {sorted.map((it) => (
            <label key={it.id} className="inline-flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={selSet.has(it.id)}
                onChange={(e) => toggle(it.id, e.target.checked)}
              />
              <span>
                <strong>{it.displayName}</strong>
                <span className="ml-1 text-slate-400">({it.id})</span>
                {it.mode !== props.mode && (
                  <span className="ml-1 text-slate-400">［{getModeLabel(it.mode)}］</span>
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 既存地区の入力項目 (dataSettings.userFields / postFields) を取込むボタン。
 * useDistrict は hook のため、地区選択後に本コンポーネントを mount して単一地区を購読する。
 */
function DistrictFieldsImporter(props: {
  districtId: string;
  onImport: (userFieldsJson: string, postFieldsJson: string) => void;
}) {
  const state = useDistrict(props.districtId);
  if (state.status === 'loading') {
    return <span className="text-xs text-slate-400">地区を読込中…</span>;
  }
  if (state.status !== 'ready') {
    return <span className="text-xs text-danger-600">地区を読込めませんでした</span>;
  }
  const ds = state.district.dataSettings;
  const uf = ds?.userFields ?? [];
  const pf = ds?.postFields ?? [];
  const has = uf.length > 0 || pf.length > 0;
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={!has}
      onClick={() => props.onImport(JSON.stringify(uf, null, 2), JSON.stringify(pf, null, 2))}
    >
      {has
        ? `この地区の入力項目を取込 (ユーザー ${uf.length}・投稿 ${pf.length})`
        : 'この地区には入力項目設定がありません'}
    </Button>
  );
}

function validateModeProfile(raw: unknown): ModeProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.mode !== 'string') return null;
  if (typeof r.name !== 'string') return null;
  if (typeof r.profileVersion !== 'string') return null;
  if (!Array.isArray(r.userFields) || !Array.isArray(r.postFields)) return null;
  return r as unknown as ModeProfile;
}

export function ModeProfilesPage() {
  // ドキュメント ID の重複判定 (アーカイブ済との衝突も検出) のためアーカイブ済も購読する。
  // 一覧に出すのは従来どおり有効な行のみ (activeItems)。
  const state = useModeProfiles({ filterArchived: false });
  const districts = useDistricts();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyTarget, setApplyTarget] = useState<ModeProfile | null>(null);
  const [importDistrictId, setImportDistrictId] = useState('');
  /**
   * 「別名で保存」直後 (未保存の複製) の複製元プロファイル名 (null=複製状態でない)。
   * 複製注記の表示条件に使う。保存 / 別行の編集 / 新規作成でクリアする。
   */
  const [duplicatedFrom, setDuplicatedFrom] = useState<string | null>(null);
  /**
   * 新規作成中のドキュメント ID を管理者が手で編集したか。
   * true の間は名称を変えても ID を自動生成し直さない (手入力を上書きしないため)。
   */
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  /** ドキュメント ID の入力欄に一度でも触れたか (未入力エラーの表示条件)。 */
  const [idTouched, setIdTouched] = useState(false);

  // 推奨マスタ dropdown 用の各マスタ一覧。
  const m1State = useAiAnalysisSettings();
  const m2State = useAiCategoryMasters();
  const m3State = useAiOutputFormats();
  const m4State = useAiDialogueSettings();
  const m5State = useAnalysisDisplayConfigs();
  const m6State = useScoreDimensionMasters();
  const m1Items: MasterOption[] = m1State.status === 'ready' ? m1State.items : [];
  const m2Items: MasterOption[] = m2State.status === 'ready' ? m2State.items : [];
  const m3Items: MasterOption[] = m3State.status === 'ready' ? m3State.items : [];
  const m4Items: MasterOption[] = m4State.status === 'ready' ? m4State.items : [];
  const m5Items: MasterOption[] = m5State.status === 'ready' ? m5State.items : [];
  const m6Items: MasterOption[] = m6State.status === 'ready' ? m6State.items : [];
  const districtItems = districts.status === 'ready' ? districts.items : [];

  /** 全プロファイル (アーカイブ済込み)。ID 重複判定と取込時の既存判定に使う。 */
  const allProfiles = state.status === 'ready' ? state.items : [];
  /** 有効なプロファイル (一覧表示・書き出し対象)。 */
  const activeProfiles = allProfiles.filter((p) => !p.archivedAt);
  const archivedCount = allProfiles.length - activeProfiles.length;
  /**
   * 新規 id (slug) の衝突判定に使う既存 id 集合。
   * **アーカイブ済も必ず含める** (新規保存は全置換なので、アーカイブ済と同 id で保存すると
   * 復元不能な上書きになる)。
   */
  const existingIds = allProfiles.map((p) => p.id);

  /** プロファイル id → 使用状況 (そのプロファイルを採用した地区)。 */
  const usageByProfile = groupModeProfileUsage({ districts: districtItems });
  /** 使用状況の読込が終わったか (未完了のうちは「未使用」と断定しない)。 */
  const usageReady = districts.status === 'ready';

  /** 推奨マスタ id → 表示名の逆引き (連携テンプレートのバッジ表示用)。 */
  const masterNameById = new Map<string, string>();
  for (const list of [m1Items, m2Items, m3Items, m4Items, m5Items, m6Items]) {
    for (const it of list) masterNameById.set(it.id, it.displayName || it.id);
  }
  /**
   * 参照先マスタ一覧の読込が全て終わったか。
   * 読込中に「見つかりません」を出すと、実在するマスタを欠番と誤解させるため表示条件に使う。
   */
  const mastersReady = [m1State, m2State, m3State, m4State, m5State, m6State].every(
    (st) => st.status === 'ready',
  );

  async function handleImport(importedItems: ModeProfile[]) {
    for (const profile of importedItems) {
      const existing = allProfiles.find((x) => x.id === profile.id);
      // Sprint BG-MasterIO follow-up — 共通 helper に統一 (旧手書き destructure を置換)
      const { id, ...rest } = stripSystemFields(profile as unknown as Record<string, unknown>);
      await upsertModeProfile({
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as unknown as Omit<
          ModeProfile,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt'
        >,
      });
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT });
    setIsNew(true);
    setImportDistrictId('');
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleEdit(profile: ModeProfile) {
    setEditing(draftFromProfile(profile));
    setIsNew(false);
    setImportDistrictId('');
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  function handleCancel() {
    setEditing(null);
    setIsNew(false);
    setImportDistrictId('');
    setDuplicatedFrom(null);
    setIdManuallyEdited(false);
    setIdTouched(false);
  }

  /**
   * 別名保存 (複製) の共通処理: 渡された draft の内容を丸ごと引き継いだ新規プロファイルの
   * 下書きを用意する。**この時点では保存しない** (誤操作で意図しないプロファイルが増えないよう、
   * 確定は「保存」ボタンに委ねる)。
   *
   * M1/M3/M5 の `duplicateDraft` は `displayName` を名前欄に持つ draft 前提だが、
   * モードプロファイルの名前欄は `name` なのでここで同等の処理を組み立てる
   * (deep copy / 「〜 のコピー」/ 元 id + `-copy` の採番は共通ヘルパを再利用)。
   */
  function applyDuplicate(source: FormDraft) {
    const copy = deepCloneDraft(source);
    setEditing({
      ...copy,
      name: `${source.name}${DUPLICATE_NAME_SUFFIX}`,
      id: uniqueSlug(`${source.id}-copy`, existingIds),
    });
    setIsNew(true);
    setImportDistrictId('');
    setDuplicatedFrom(source.name);
    setIdManuallyEdited(false); // 複製直後は `{元 id}-copy`。手入力すればそこで固定される
    setIdTouched(false);
    toast(
      'success',
      `「${source.name}」の内容を複製しました。名称とドキュメント ID を確認して「保存」を押してください。`,
    );
  }

  /** 編集中のプロファイルを別名で保存する (未保存の編集内容も含めて引き継ぐ)。 */
  function handleDuplicateCurrent() {
    if (!editing) return;
    applyDuplicate(editing);
  }

  /** 一覧の行から複製する (その行を読み込んだうえで別名保存と同じ状態にする)。 */
  function handleDuplicateFromRow(profile: ModeProfile) {
    applyDuplicate(draftFromProfile(profile));
  }

  /**
   * 名称変更: 新規時かつ ID 未編集なら id を slug 自動生成する。
   * 管理者が ID を手入力済み (idManuallyEdited) のときは、その ID を上書きしない。
   */
  function setName(name: string) {
    if (!editing) return;
    if (isNew && !idManuallyEdited) {
      setEditing({ ...editing, name, id: uniqueSlug(name, existingIds) });
    } else {
      setEditing({ ...editing, name });
    }
  }

  /** ドキュメント ID の手入力 (新規時のみ)。以後は名称変更で自動生成し直さない。 */
  function setDocumentId(id: string) {
    if (!editing) return;
    setIdManuallyEdited(true);
    setIdTouched(true);
    setEditing({ ...editing, id });
  }

  /** ドキュメント ID を名称からの自動生成に戻す。 */
  function regenerateDocumentId() {
    if (!editing) return;
    setIdManuallyEdited(false);
    setEditing({ ...editing, id: uniqueSlug(editing.name, existingIds) });
  }

  /** 新規作成中のドキュメント ID の問題 (未入力 / 使用不可文字 / 既存重複)。 */
  const idError =
    editing && isNew ? validateDocumentId(editing.id, existingIds, 'モードプロファイル') : null;
  /**
   * 入力欄の下に赤字で出すエラー。未入力は一度も触っていない間は出さない
   * (開いた直後から赤字だと壊れて見えるため)。
   */
  const visibleIdError =
    idError == null || (!idTouched && !(editing?.id ?? '').trim()) ? null : idError;

  function handleImportFromTemplate() {
    if (!editing) return;
    const tpl = getModeTemplate(editing.mode);
    setEditing({
      ...editing,
      userFieldsJson: JSON.stringify(tpl.dataSettings.userFields, null, 2),
      postFieldsJson: JSON.stringify(tpl.dataSettings.postFields, null, 2),
    });
    toast(
      'success',
      `${getModeLabel(editing.mode, editing.modeLabel)} の現行テンプレを読込みました`,
    );
  }

  function handleImportFromDistrict(userFieldsJson: string, postFieldsJson: string) {
    if (!editing) return;
    setEditing({ ...editing, userFieldsJson, postFieldsJson });
    toast('success', '選択した地区の入力項目を読込みました');
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.name.trim()) {
      toast('error', 'name は必須です');
      return;
    }
    if (!editing.mode.trim()) {
      toast('error', 'mode は必須です (組込モード or 新しい key)');
      return;
    }
    if (!editing.profileVersion.trim()) {
      toast('error', 'profileVersion は必須です (例: ecw-2026-05)');
      return;
    }
    if (isNew) {
      // 最終防御 (入力欄の赤字表示と同じ判定)。アーカイブ済との重複もここで弾く。
      setIdTouched(true);
      const idErr = validateDocumentId(editing.id, existingIds, 'モードプロファイル');
      if (idErr) {
        toast('error', idErr);
        return;
      }
    }
    let userFields: FieldDef[];
    let postFields: FieldDef[];
    try {
      const u = JSON.parse(editing.userFieldsJson);
      if (!Array.isArray(u)) throw new Error('userFields は配列である必要があります');
      userFields = u as FieldDef[];
    } catch (e) {
      toast('error', `userFields の JSON が不正: ${(e as Error).message}`);
      return;
    }
    try {
      const p = JSON.parse(editing.postFieldsJson);
      if (!Array.isArray(p)) throw new Error('postFields は配列である必要があります');
      postFields = p as FieldDef[];
    } catch (e) {
      toast('error', `postFields の JSON が不正: ${(e as Error).message}`);
      return;
    }
    setSaving(true);
    try {
      const data: Omit<
        ModeProfile,
        'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt'
      > = {
        mode: editing.mode.trim(),
        name: editing.name.trim(),
        profileVersion: editing.profileVersion.trim(),
        userFields,
        postFields,
      };
      const modeLabel = editing.modeLabel.trim();
      if (modeLabel) data.modeLabel = modeLabel;
      const description = editing.description.trim();
      if (description) data.description = description;
      const recommendedMasters = buildRecommendedMasters(editing);
      if (recommendedMasters) data.recommendedMasters = recommendedMasters;

      await upsertModeProfile({
        id: editing.id.trim(),
        uid: 'admin-stub', // 既存 master 系と同じ stub uid。実 master role uid は別 sprint で接続。
        isNew,
        data,
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      handleCancel();
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveModeProfile(id, 'admin-stub');
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">モードプロファイル</h1>
        <p className="text-sm text-slate-500 mt-1">
          新地区作成時の初期値 (userFields / postFields / 推奨マスタ ID) を Firestore 駆動で管理。
          旧来の static `modeTemplates.ts` の動的化版。組込 4 モードに加え、新しいモード key (例:
          fukushi) を自由に作成できます。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<ModeProfile>
          collectionName="modeProfiles"
          items={activeProfiles}
          validator={validateModeProfile}
          onImport={handleImport}
          disabled={editing !== null}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {activeProfiles.length} (アーカイブ済除外)
            {archivedCount > 0 && (
              <span className="ml-2">
                ／ アーカイブ済 {archivedCount} 件（ドキュメント ID の重複判定に含めます）
              </span>
            )}
          </p>
          {/* grid-cols-1 = repeat(1, minmax(0,1fr))。素の `grid` だと列トラックの最小幅が
              行の min-content になり、中身が長い行があると一覧全体が画面幅を超えて右が切れる。 */}
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {activeProfiles.map((profile) => {
              const usage = usageByProfile[profile.id];
              const unused = usageReady && !usage;
              return (
                <li key={profile.id}>
                  <Card padding="md" className="flex items-start justify-between gap-3 flex-wrap">
                    {/* スマホ幅 (< 640px) では 260px 下限を外して縮められるようにする。
                        下限を常時 260px にすると、狭い画面でカードの中身が枠外へはみ出す。 */}
                    <div className="min-w-0 sm:min-w-[260px] flex-1">
                      <div>
                        <strong className="text-slate-900">{profile.name}</strong>
                        <span className="ml-2 text-xs text-slate-500">id: {profile.id}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          モード: {getModeLabel(profile.mode, profile.modeLabel)}
                        </span>
                        <span className="ml-2 text-xs text-slate-500">
                          version: {profile.profileVersion}
                        </span>
                        {unused && (
                          <span
                            className="ml-2 inline-block px-1.5 py-0.5 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded"
                            title="このプロファイルを採用した地区はありません。アーカイブしても既存地区への影響はありません。"
                          >
                            採用地区なし
                          </span>
                        )}
                      </div>
                      {profile.description && (
                        <p className="text-xs text-slate-500 mt-1 truncate">
                          {profile.description}
                        </p>
                      )}
                      <ProfileUsageSummary
                        usage={usage}
                        ready={usageReady}
                        currentVersion={profile.profileVersion}
                        recommended={profile.recommendedMasters}
                        masterNameById={masterNameById}
                        mastersReady={mastersReady}
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <RowExportButton
                        collectionName="modeProfiles"
                        item={{ ...profile, displayName: profile.name }}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(profile)}
                        aria-label={`${profile.name} を編集`}
                      >
                        編集
                      </Button>
                      {/* アーカイブ済は一覧に出さないため、複製は常に有効な行だけに出る。 */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDuplicateFromRow(profile)}
                        aria-label={`${profile.name} を複製`}
                        title="このモードプロファイルの内容を引き継いだ新しいプロファイルの下書きを作ります（この操作では保存されません）"
                      >
                        📋 複製
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setApplyTarget(profile)}
                        aria-label={`${profile.name} を地区へ反映`}
                      >
                        地区へ反映
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => void handleArchive(profile.id, profile.name)}
                        aria-label={`${profile.name} をアーカイブ`}
                      >
                        アーカイブ
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          {duplicatedFrom != null && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-bold">
                「{duplicatedFrom}」を複製した、まだ保存していないモードプロファイルです。
              </p>
              <p className="mt-1">
                名称とドキュメント ID
                を確認して「保存」を押すと、新しいモードプロファイルとして作成されます。ここでの
                編集は複製元には影響しません。
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-id">
                ドキュメント ID (id){' '}
                {isNew ? (
                  <span className="text-danger-600">*</span>
                ) : (
                  <span className="text-slate-400">(変更できません)</span>
                )}
              </label>
              <div className="flex gap-2 items-start">
                <input
                  id="mp-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setDocumentId(e.target.value)}
                  onBlur={() => setIdTouched(true)}
                  readOnly={!isNew}
                  aria-invalid={visibleIdError ? true : undefined}
                  className={`${INPUT_CLASS} flex-1 font-mono ${
                    !isNew ? 'bg-slate-100 text-slate-600' : ''
                  }`}
                  placeholder="例: ecw-2026-05"
                  spellCheck={false}
                />
                {isNew && idManuallyEdited && (
                  <Button variant="secondary" size="sm" onClick={regenerateDocumentId}>
                    名称から自動生成
                  </Button>
                )}
              </div>
              {isNew ? (
                <>
                  {visibleIdError && (
                    <p className="mt-1 text-[11px] text-danger-600">{visibleIdError}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                    半角の英小文字・数字・ハイフン (-) が使えます (例: ecw-2026-05)。
                    プロファイル名から自動で作られるので、決めたい ID
                    があるときだけ書き換えてください。
                    <span className="font-bold">保存すると後から変更できません</span>
                    （地区はこの ID で採用元プロファイルを記録します）。
                  </p>
                </>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                  地区が採用元プロファイルを記録するための ID です。作成後は変更できません。別の ID
                  にしたいときは「別名で保存」で新しいプロファイルを作り、そちらに ID
                  を付けてください。
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-mode">
                モード (mode、key) <span className="text-danger-600">*</span>
              </label>
              <input
                id="mp-mode"
                type="text"
                list="mp-mode-builtin"
                value={editing.mode}
                onChange={(e) => setEditing({ ...editing, mode: e.target.value })}
                className={INPUT_CLASS}
                placeholder="例: ecw / bousai / fukushi (自由入力可)"
              />
              <datalist id="mp-mode-builtin">
                {DISTRICT_MODES.map((m) => (
                  <option key={m} value={m}>
                    {getModeLabel(m)}
                  </option>
                ))}
              </datalist>
              <p className="text-[11px] text-slate-500 mt-1">
                組込モード (ecw / bousai / walker / kids-bousai)
                はリストから選べます。新しいモードは 英小文字とハイフンで自由に入力できます (例:
                fukushi)。分類・地図色付け・AI 解析などは
                新モードでは地区作成後に個別設定が必要です。
              </p>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-modelabel">
                モード表示名 (modeLabel、日本語、任意。新しいモードでは推奨)
              </label>
              <input
                id="mp-modelabel"
                type="text"
                value={editing.modeLabel}
                onChange={(e) => setEditing({ ...editing, modeLabel: e.target.value })}
                className={INPUT_CLASS}
                placeholder="例: 福祉モード"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                利用者画面にモード名を表示する際に使います。未設定だと key
                がそのまま表示されます。組込モードは固定の表示名が優先されます。
              </p>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-name">
                プロファイル名 (name、表示名) <span className="text-danger-600">*</span>
              </label>
              <input
                id="mp-name"
                type="text"
                value={editing.name}
                onChange={(e) => setName(e.target.value)}
                className={INPUT_CLASS}
                placeholder="例: 街歩き 2026-05"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-description">
                説明 (description、補足、optional)
              </label>
              <textarea
                id="mp-description"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                rows={2}
                className={`${INPUT_CLASS} resize-y`}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-version">
                バージョン (profileVersion、snapshot 識別子){' '}
                <span className="text-danger-600">*</span>
              </label>
              <input
                id="mp-version"
                type="text"
                value={editing.profileVersion}
                onChange={(e) => setEditing({ ...editing, profileVersion: e.target.value })}
                className={INPUT_CLASS}
                placeholder="例: ecw-2026-05"
              />
            </div>

            <div className="border-t border-slate-200 pt-3 mt-2 grid grid-cols-1 gap-2 min-w-0">
              <p className="text-xs text-slate-500">
                入力項目 (userFields / postFields) は JSON
                で編集します。下のボタンで初期値を流し込めます。
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="secondary" size="sm" onClick={handleImportFromTemplate}>
                  ⤓ 現在のテンプレから取込 ({getModeLabel(editing.mode, editing.modeLabel)})
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">既存地区から取込:</span>
                <select
                  value={importDistrictId}
                  onChange={(e) => setImportDistrictId(e.target.value)}
                  className={`${INPUT_CLASS} min-w-0`}
                  /* width:auto は選択肢 (= 地区名、可変長) の最長に合わせて伸びるため、
                     地区名が長い / 地区数が多いと画面幅を超えて右が見えなくなる。
                     maxWidth:100% で親の幅に収め、あふれる分は省略表示に任せる。 */
                  style={{ width: 'auto', maxWidth: '100%' }}
                  aria-label="取込元の地区を選択"
                >
                  <option value="">(地区を選択)</option>
                  {districtItems.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({getModeLabel(d.mode)})
                    </option>
                  ))}
                </select>
                {importDistrictId && (
                  <DistrictFieldsImporter
                    districtId={importDistrictId}
                    onImport={handleImportFromDistrict}
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-userfields">
                ユーザー入力項目 (userFields、FieldDef[] JSON)
              </label>
              <textarea
                id="mp-userfields"
                value={editing.userFieldsJson}
                onChange={(e) => setEditing({ ...editing, userFieldsJson: e.target.value })}
                rows={10}
                className={TEXTAREA_CLASS}
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="mp-postfields">
                投稿入力項目 (postFields、FieldDef[] JSON)
              </label>
              <textarea
                id="mp-postfields"
                value={editing.postFieldsJson}
                onChange={(e) => setEditing({ ...editing, postFieldsJson: e.target.value })}
                rows={10}
                className={TEXTAREA_CLASS}
              />
            </div>

            <fieldset className="border border-slate-200 rounded-md p-3">
              <legend className="text-xs font-bold text-slate-600">
                推奨マスタ (新地区作成時に District 各 field にコピー、未選択で省略)
              </legend>
              <div className="grid grid-cols-1 gap-3 mt-1">
                <MasterSelect
                  label="推奨 AI 解析設定 (M1)"
                  value={editing.recommendedAnalysisSettingsId}
                  onChange={(v) => setEditing({ ...editing, recommendedAnalysisSettingsId: v })}
                  items={m1Items}
                  mode={editing.mode}
                  showModel
                />
                <MasterSelect
                  label="推奨 AI 対話設定 (M4)"
                  value={editing.recommendedDialogueSettingsId}
                  onChange={(v) => setEditing({ ...editing, recommendedDialogueSettingsId: v })}
                  items={m4Items}
                  mode={editing.mode}
                />
                <MasterSelect
                  label="推奨 分析表示設定 (M5)"
                  value={editing.recommendedDisplayConfigId}
                  onChange={(v) => setEditing({ ...editing, recommendedDisplayConfigId: v })}
                  items={m5Items}
                  mode={editing.mode}
                />
                <MasterSelect
                  label="推奨 AI 出力フォーマット (M3)"
                  value={editing.recommendedOutputFormatId}
                  onChange={(v) => setEditing({ ...editing, recommendedOutputFormatId: v })}
                  items={m3Items}
                  mode={editing.mode}
                />
                <MasterMultiSelect
                  label="推奨 AI カテゴリマスタ (M2、複数選択可)"
                  selectedCsv={editing.recommendedCategoryMasterIds}
                  onChange={(v) => setEditing({ ...editing, recommendedCategoryMasterIds: v })}
                  items={m2Items}
                  mode={editing.mode}
                />
                <MasterSelect
                  label="推奨 スコア軸 (M6)"
                  value={editing.recommendedScoreDimensionId}
                  onChange={(v) => setEditing({ ...editing, recommendedScoreDimensionId: v })}
                  items={m6Items}
                  mode={editing.mode}
                />
              </div>
            </fieldset>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button variant="secondary" size="md" onClick={handleCancel} disabled={saving}>
                キャンセル
              </Button>
              {!isNew && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleDuplicateCurrent}
                  disabled={saving}
                  title="いま編集している内容を引き継いで、新しいモードプロファイルの下書きを作ります（この操作では保存されません）"
                >
                  別名で保存
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving || visibleIdError != null}
                title={
                  visibleIdError != null
                    ? 'ドキュメント ID を修正すると保存できます'
                    : '編集内容を保存します'
                }
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {applyTarget && (
        <ModeProfileApplyModal
          profile={applyTarget}
          districtsState={districts}
          onClose={() => setApplyTarget(null)}
        />
      )}
    </div>
  );
}

/** 推奨マスタ 1 種類あたりのバッジ行 (ラベル + 参照先 id 群)。 */
interface RecommendedRow {
  label: string;
  ids: string[];
}

/**
 * `recommendedMasters` を「連携テンプレート」の行データへ展開する。
 * 未設定の種別は行ごと出さない (空行でカードが間延びしないよう)。
 */
function recommendedRows(rec: ModeProfileRecommendedMasters | undefined): RecommendedRow[] {
  if (!rec) return [];
  const rows: RecommendedRow[] = [
    { label: 'AI 解析テンプレート', ids: rec.analysisSettingsId ? [rec.analysisSettingsId] : [] },
    { label: 'AI 出力フォーマット', ids: rec.outputFormatId ? [rec.outputFormatId] : [] },
    { label: 'カテゴリマスタ', ids: rec.categoryMasterIds ?? [] },
    { label: '分析表示設定', ids: rec.displayConfigId ? [rec.displayConfigId] : [] },
    { label: 'AI 対話設定', ids: rec.dialogueSettingsId ? [rec.dialogueSettingsId] : [] },
    { label: 'スコア軸', ids: rec.scoreDimensionId ? [rec.scoreDimensionId] : [] },
  ];
  return rows.filter((r) => r.ids.length > 0);
}

/**
 * 一覧行に出す「採用地区 / 連携テンプレート」。
 *
 * モードプロファイル (M10) は 2 方向の関係を持つ:
 *   - 下流: このプロファイルを採用した地区 (`District.profileId`、新地区作成 / 「地区へ反映」時に記録)
 *   - 上流: 新地区へ推奨する各マスタ (`recommendedMasters`、地区作成時に District 各 field へ転写)
 *
 * 採用地区は「採用した時点の版」も記録されるので、プロファイルを更新した後で版差がある地区には
 * 注記を添える (地区側は自動追従しないため、再反映が要ることが一目で分かるように)。
 */
function ProfileUsageSummary(props: {
  usage: ModeProfileUsage | undefined;
  ready: boolean;
  /** プロファイルの現行版 (地区が採用した版との差を出すため)。 */
  currentVersion: string;
  recommended: ModeProfileRecommendedMasters | undefined;
  /** 推奨マスタ id → 表示名。未ロード / 欠番は id のまま出す。 */
  masterNameById: Map<string, string>;
  /** 参照先マスタ一覧の読込が終わったか (未完了のうちは「見つかりません」と断定しない)。 */
  mastersReady: boolean;
}) {
  const { usage, ready, currentVersion, recommended, masterNameById, mastersReady } = props;

  const districtBadges: UsageBadgeItem[] = (usage?.districts ?? []).map((d) => {
    const drifted = d.appliedVersion != null && d.appliedVersion !== currentVersion;
    return {
      key: d.id,
      label: d.name,
      note: drifted ? `採用版 ${d.appliedVersion}` : undefined,
      tone: 'district',
      title: drifted
        ? `この地区は ${d.appliedVersion} を採用したままです (現行 ${currentVersion})。差分を反映するには「地区へ反映」を実行してください`
        : `この地区がこのプロファイルを採用しています (モード: ${getModeLabel(d.mode, d.modeLabel)})`,
    };
  });

  const rows = recommendedRows(recommended);

  return (
    // 狭い画面では見出し (dt) と中身 (dd) を折り返して縦に積む。dd に min-w-0 が無いと
    // バッジの最小幅で dd が広がり、カードごと画面幅を超える。
    <dl className="mt-2 grid grid-cols-1 gap-1 min-w-0">
      <div className="flex gap-2 items-start flex-wrap">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">採用地区</dt>
        <dd className="flex-1 min-w-0">
          <UsageBadges
            items={districtBadges}
            emptyLabel={ready ? 'このプロファイルを採用した地区はありません' : '読込中…'}
          />
        </dd>
      </div>
      <div className="flex gap-2 items-start flex-wrap">
        <dt className="w-[7rem] shrink-0 text-xs text-slate-500">連携テンプレート</dt>
        <dd className="flex-1 min-w-0">
          {rows.length === 0 ? (
            <span className="text-slate-400 text-xs">推奨マスタは設定されていません</span>
          ) : (
            <div className="grid grid-cols-1 gap-0.5 min-w-0">
              {rows.map((row) => (
                <div key={row.label} className="flex gap-1.5 items-start flex-wrap min-w-0">
                  <span className="text-[11px] text-slate-400 shrink-0">{row.label}:</span>
                  <UsageBadges
                    items={row.ids.map((id) => {
                      const name = masterNameById.get(id);
                      const missing = mastersReady && name === undefined;
                      return {
                        key: `${row.label}:${id}`,
                        label: name ?? id,
                        note: missing ? '見つかりません' : undefined,
                        tone: missing ? 'muted' : 'template',
                        title: missing
                          ? `参照先のマスタ (${id}) が見つかりません。アーカイブ済か、削除された可能性があります`
                          : `新規地区作成時にこのマスタ (${id}) が地区へ設定されます`,
                      };
                    })}
                  />
                </div>
              ))}
            </div>
          )}
        </dd>
      </div>
    </dl>
  );
}
